import React, { Suspense } from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useParams } from '@reach/router';
import { MutationResult, QueryResult } from '@apollo/client';

import { Spinner } from '$/images/Icons';
import config from '$/config';

import { RedirectIfUnauthenticated } from './RedirectIfUnauthenticated';
import { logger } from '$/utils/logger';

const NotFoundPage = React.lazy(() => import('$/components/pages/404/404'));
const NotConnectedPage = React.lazy(() => import('$/components/pages/404/NotConnectedPage'));
const NoWebinar = React.lazy(() => import('$/components/pages/404/NoWebinar'));
const GraphqlErrorPage = React.lazy(() => import('$/components/pages/404/GraphqlErrorPage'));

export enum LoadingErrors {
  WebinarNotAvailable = 'WebinarNotAvailable',
  VideoNotAvailable = 'VideoNotAvailable',
}
interface LoadingProps {
  query?: QueryResult<any, any> | MutationResult<any>;
  error?: LoadingErrors;
  local?: boolean;
  hidden?: boolean;
  className?: string;
  position?: string;
  blurred?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  query,
  error,
  local,
  hidden,
  className = '',
  position = 'middle',
  blurred,
}) => {
  const styledSpinner = React.useMemo(() => {
    return (
      <Spinner
        alt='Loading'
        className={className}
        css={[
          css`
            ${tw`text-color-brand`}
            max-width: 30px;
            max-height: 30px;
            width: 50%;
            height: 50%;
          `,
          position === 'bottom'
            ? css`
                position: absolute;
                bottom: 8px;
                top: initial;
                left: 50%;
                width: 1.2rem;
                height: 1.2rem;
                transform: translateX(-50%);
              `
            : local
              ? css`
                  margin: auto;
                `
              : css`
                  position: fixed;
                  top: calc(50% - 1.75rem);
                  left: calc(50% - 1.75rem);
                `,
        ]}
      />
    );
  }, [className, local, position]);

  const params = useParams();
  if (error) {
    logger.error('Loading Error: ', { query, error });

    switch (error) {
      case LoadingErrors.WebinarNotAvailable:
        return (
          <Suspense>
            <NoWebinar />
          </Suspense>
        );
      default:
        return (
          <Suspense>
            <NotFoundPage />
          </Suspense>
        );
    }
  }

  if (query && query.error) {
    // TODO: Handle no network error vs not found error vs unauthenticated error
    console.error('Query Error: ', { query });

    if (query.error.graphQLErrors) {
      const extensions = query.error.graphQLErrors[0]?.extensions;
      switch (extensions?.code) {
        case 'FORBIDDEN':
          return <RedirectIfUnauthenticated />;
        case 'NOT_FOUND':
          return (
            <Suspense>
              <NoWebinar />
            </Suspense>
          );
        default:
          // @ts-ignore
          if (extensions?.exception?.name === 'EntityNotFound') {
            return (
              <Suspense>
                <NotFoundPage />
              </Suspense>
            );
          }
          if (params?.attendeeId === '0') {
            document.location.href = `${config.URL_MARKETING_SITE}/test-email-links`;
            return null;
          }
          return (
            <Suspense>
              <GraphqlErrorPage error={query.error.graphQLErrors[0]} />
            </Suspense>
          );
      }
    }

    if (query.error.networkError) {
      return (
        <Suspense>
          <NotConnectedPage />
        </Suspense>
      );
    }

    return (
      <Suspense>
        <NotFoundPage />
      </Suspense>
    );
  }

  if (hidden) {
    return null;
  }

  if (blurred) {
    return (
      <div
        className='fixed z-modal top-0 left-0 w-full h-full'
        css={css`
          background: rgba(0, 0, 0, 0.05);
          backdrop-filter: blur(2px);
        `}
      >
        {styledSpinner}
      </div>
    );
  }

  return styledSpinner;
};

export default Loading;
